/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { FaRegTrashAlt } from "react-icons/fa";

interface Entry {
  routerName: string;
  macAddress: string;
  publicIp: string;
}

interface Props {
  onSubmit: (entries: Entry[]) => void;
  onCancel?: () => void;
  saving: boolean;
}

const AddRouterForm: React.FC<Props> = ({ onCancel,onSubmit, saving }) => {
  const [entries, setEntries] = useState<Entry[]>([
    { routerName: "", macAddress: "", publicIp: "" },
  ]);
  const initialEntries = useMemo<Entry[]>(
    () => [{ routerName: "", macAddress: "", publicIp: "" }],
    []
  );
  const [errors, setErrors] = useState<any[]>([{}]);

  // Regexes
  const macRegex = /^([0-9A-Fa-f]{2}([-:]?)){5}[0-9A-Fa-f]{2}$/;
  const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

  const sanitizeRouterName = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.target.value = e.target.value.replace(/[^a-zA-Z0-9\s,._]/g, "");
  };

  const sanitizeMacAddress = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.target.value = e.target.value.replace(/[^a-fA-F0-9:]/g, "");
  };

  const sanitizeIpAddress = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.target.value = e.target.value.replace(/[^0-9.]/g, "");
  };

  const handleChange = (
    i: number,
    key: keyof Entry,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;

    const updated = [...entries];
    updated[i][key] = value;
    setEntries(updated);

    const updatedErrors = [...errors];

    if (key === "routerName") {
      updatedErrors[i].routerName = "";
    }

    if (key === "macAddress") {
      if (!value) {
        updatedErrors[i].macAddress = "MAC address is required";
      } else if (!macRegex.test(value.trim())) {
        updatedErrors[i].macAddress = "Enter a valid MAC address";
      } else {
        updatedErrors[i].macAddress = "";
      }
    }

    if (key === "publicIp") {
      if (!value) {
        updatedErrors[i].publicIp = "Public IP is required";
      } else if (!ipRegex.test(value.trim())) {
        updatedErrors[i].publicIp = "Enter a valid IP address";
      } else {
        updatedErrors[i].publicIp = "";
      }
    }

    setErrors(updatedErrors);
  };

  const addRow = () => {
    setEntries([...entries, { routerName: "", macAddress: "", publicIp: "" }]);
    setErrors([...errors, {}]);
  };

  const removeRow = (i: number) => {
    if (entries.length === 1) return;
    setEntries(entries.filter((_, idx) => idx !== i));
    setErrors(errors.filter((_, idx) => idx !== i));
  };

  const getRouterNameError = (routerName: string) => {
    if (!routerName) return "Router name is required";
    if (routerName !== routerName.trim()) return "Enter a valid router name";
    return "";
  };

  const getMacAddressError = (macAddress: string) => {
    if (!macAddress) return "MAC address is required";
    if (macAddress !== macAddress.trim()) return "Enter a valid MAC address";
    if (!macRegex.test(macAddress.trim())) return "Enter a valid MAC address";
    return "";
  };

  const getPublicIpError = (publicIp: string) => {
    if (!publicIp) return "Public IP is required";
    if (!ipRegex.test(publicIp.trim())) return "Enter a valid IP address";
    return "";
  };

  const validate = () => {
    const newErrors = entries.map((e) => ({
      routerName: getRouterNameError(e.routerName),
      macAddress: getMacAddressError(e.macAddress),
      publicIp: getPublicIpError(e.publicIp),
    }));

    setErrors(newErrors);

    return newErrors.every(
      (e) => !e.routerName && !e.macAddress && !e.publicIp
    );
  };

  const isChanged = useMemo(() => {
    if (entries.length !== initialEntries.length) return true;

    return entries.some((entry, idx) => {
      const initial = initialEntries[idx];
      if (!initial) return true;
      return (
        entry.routerName.trim() !== initial.routerName.trim() ||
        entry.macAddress.trim() !== initial.macAddress.trim() ||
        entry.publicIp.trim() !== initial.publicIp.trim()
      );
    });
  }, [entries, initialEntries]);

  const isFormValidForSubmit = useMemo(() => {
    return entries.every((entry) => {
      const routerName = entry.routerName;
      const macAddress = entry.macAddress;
      const publicIp = entry.publicIp;

      const isRouterNameValid = !!routerName && routerName === routerName.trim();
      const isMacValid = !!macAddress && macAddress === macAddress.trim() && macRegex.test(macAddress.trim());
      const isIpValid = !!publicIp && ipRegex.test(publicIp.trim());

      return isRouterNameValid && isMacValid && isIpValid;
    });
  }, [entries]);

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit(entries);
  };

  return (
    <div className="p-5 space-y-4">
      {entries.map((entry, i) => (
        <div key={i} className="flex gap-3 items-start">
          {/* Router Name */}
          <div className="flex-1">
            {i === 0 && (
              <label htmlFor={`router-name-${i}`} className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                Router Name <span className="text-red-500">*</span>
              </label>
            )}
            <input
              id={`router-name-${i}`}
              placeholder="Router Name"
              value={entry.routerName}
              onChange={(e) => {
                sanitizeRouterName(e);
                handleChange(i, "routerName", e);
              }}
              className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 focus:ring-1 focus:ring-primary"
            />
            {errors[i]?.routerName && (
              <p className="text-red-500 text-xs mt-1">
                {errors[i].routerName}
              </p>
            )}
          </div>

          {/* MAC Address */}
          <div className="flex-1">
            {i === 0 && (
              <label htmlFor={`router-mac-${i}`} className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                MAC Address <span className="text-red-500">*</span>
              </label>
            )}
            <input
              id={`router-mac-${i}`}
              placeholder="MAC Address"
              value={entry.macAddress}
              onChange={(e) => {
                sanitizeMacAddress(e);
                handleChange(i, "macAddress", e);
              }}
              className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 focus:ring-1 focus:ring-primary"
            />
            {errors[i]?.macAddress && (
              <p className="text-red-500 text-xs mt-1">
                {errors[i].macAddress}
              </p>
            )}
          </div>

          {/* Public IP */}
          <div className="flex-1">
            {i === 0 && (
              <label htmlFor={`router-ip-${i}`} className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                Public IP <span className="text-red-500">*</span>
              </label>
            )}
            <input
              id={`router-ip-${i}`}
              placeholder="e.g. 192.168.1.1"
              value={entry.publicIp}
              onChange={(e) => {
                sanitizeIpAddress(e);
                handleChange(i, "publicIp", e);
              }}
              className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 focus:ring-1 focus:ring-primary"
            />
            {errors[i]?.publicIp && (
              <p className="text-red-500 text-xs mt-1">
                {errors[i].publicIp}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className={`flex gap-1 ${i === 0 ? "mt-9" : "mt-2"}`}>
            {entries.length > 1 && (
              <button
                onClick={() => removeRow(i)}
                className="text-red-500 hover:text-red-600"
              >
                <FaRegTrashAlt className="text-error group-hover:scale-110 transition-transform" />
              </button>
            )}
            {i === entries.length - 1 && (
              <button
                onClick={addRow}
                className="text-primary hover:opacity-80"
              >
                <Plus size={16} className="text-primary group-hover:scale-110 transition-transform" />
              </button>
            )}
          </div>
        </div>
      ))}

      {/* Footer */}
      <div className="flex justify-end gap-1 pt-4 border-t border-slate-200 dark:border-slate-700">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2.5 text-sm font-medium text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 hover:text-gray-900 transition-all active:scale-95"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={!isChanged || !isFormValidForSubmit || saving}
          className={`text-white bg-blue-700 font-medium rounded-lg text-sm px-5 py-2.5 ms-3 transition-all
                    ${(!isChanged || !isFormValidForSubmit || saving)
                     ? "opacity-50 cursor-not-allowed bg-blue-700/50"
                     : "hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 active:scale-95"
            }`}

        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default AddRouterForm;